import { formatDate } from '@strata/helper-functions';
import { ReasonInput } from '@strata/react-compound-inputs';
import { DateRangeInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';

import { postAddLock } from 'api/pbmAPI';
import { handleError } from 'services/Error';
import { useGetSegmentLockConfigQuery } from 'store/api/pbmApi';
import { useGetSupplierDetailsQuery } from 'store/api/referenceApi';

const Title = 'Add/Update Lock';

const ReasonOptions = [
    'Lock missing',
    'NPI(s) missing',
    'Missing GPI/GCN/NDC',
    'Transition fill needed',
];

export const AddUpdateLockDialog = ({
    isOpen,
    onClose,
    treatmentDetails = undefined,
    submitCallback,
}) => {
    const { treatmentId, payerId, segmentId, activeReferral } = treatmentDetails ?? {};
    const { servicingSupplierNpi } = activeReferral ?? {};

    const { data: supplierDetails } = useGetSupplierDetailsQuery(
        {
            supplierNpi: servicingSupplierNpi,
        },
        {
            skip: !servicingSupplierNpi,
        },
    );

    const { data: canSetLockDates, isLoading: lockConfigLoading } = useGetSegmentLockConfigQuery(
        {
            segmentId,
        },
        {
            skip: !segmentId,
        },
    );

    return (
        <Formik
            initialValues={{}}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                ...(canSetLockDates
                    ? {
                          lockDates: DateRangeInputFormik.validationSchema,
                      }
                    : {}),
                ...ReasonInput.validationSchema,
            })}
            onSubmit={async (values, { resetForm }) => {
                try {
                    await postAddLock({
                        payerId,
                        treatmentId,
                        lockDates: values.lockDates?.map((date) => formatDate(date, 'YYYY-MM-DD')),
                        ...ReasonInput.submitValues(values),
                    });
                    submitCallback();
                    SuccessToast({ message: 'Lock added to treatment successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    const errorMessage = JSON.parse(err?.message)?.detail?.replace(/'+/g, '');
                    handleError(
                        errorMessage ?? 'Unable to add lock to treatment. Please try again.',
                        err,
                    );
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={560}
                    isOpen={isOpen}
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Add Lock"
                    isPrimaryButtonDisabled={
                        !formik.isValid || formik.isSubmitting || lockConfigLoading
                    }
                    dialogContent={
                        <>
                            <div className="margin__bottom--medium">
                                This action will add a lock for claims on this treatment for{' '}
                                <span className="font-bold">
                                    {supplierDetails?.organizationName}
                                </span>
                                .
                            </div>
                            {canSetLockDates && (
                                <DateRangeInputFormik
                                    name="lockDates"
                                    label="Lock Dates"
                                    allowSingleDayRange
                                    required
                                />
                            )}
                            <ReasonInput reasons={ReasonOptions} />
                        </>
                    }
                />
            )}
        </Formik>
    );
};

AddUpdateLockDialog.Title = Title;

AddUpdateLockDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    treatmentDetails: PropTypes.shape({
        treatmentId: PropTypes.string,
        payerId: PropTypes.string,
        activeReferral: PropTypes.shape({
            servicingSupplierNpi: PropTypes.string,
        }),
    }),
    submitCallback: PropTypes.func.isRequired,
};
